import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Star, MapPin, DollarSign, Calendar, Clock, 
  BadgeCheck, MessageCircle, ArrowLeft, Share2,
  Music, Camera, Mic2, Shield, ChefHat, Cake, Palette, Sparkles, Send, X,
  AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const categoryIcons = {
  dj: Music,
  musician: Mic2,
  singer: Mic2,
  photographer: Camera,
  caterer: ChefHat,
  baker: Cake,
  decorator: Palette,
  security: Shield,
};

const categoryImages = {
  dj: "https://images.unsplash.com/photo-1695277714272-9ec10733de30?w=800&h=600&fit=crop",
  musician: "https://images.unsplash.com/photo-1769755012867-920ce0d8f7f4?w=800&h=600&fit=crop",
  photographer: "https://images.unsplash.com/photo-1763848843590-acd070c8b9ae?w=800&h=600&fit=crop",
  caterer: "https://images.unsplash.com/photo-1764380746818-18c01e96df12?w=800&h=600&fit=crop",
};

const VendorDetail = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [sendingInquiry, setSendingInquiry] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    event_date: "",
    message: ""
  });

  useEffect(() => {
    fetchVendor();
    fetchReviews();
  }, [vendorId]);

  useEffect(() => {
    if (user) {
      setInquiryForm(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || ""
      }));
    }
  }, [user]);

  const fetchVendor = async () => {
    try {
      const response = await axios.get(`${API}/vendors/${vendorId}`);
      setVendor(response.data);
    } catch (error) {
      console.error("Error fetching vendor:", error);
      toast.error("Vendor not found");
      navigate("/vendors");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/vendor/${vendorId}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const handleBookNow = () => {
    if (!user) {
      toast.info("Please sign in to book");
      navigate("/auth", { state: { from: `/booking/${vendorId}` } });
      return;
    }
    navigate(`/booking/${vendorId}`);
  };

  const handleContact = () => {
    setShowInquiryModal(true);
  };

  const handleInquiryChange = (e) => {
    const { name, value } = e.target;
    setInquiryForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();
    setSendingInquiry(true);
    
    try {
      await axios.post(`${API}/inquiries`, {
        ...inquiryForm,
        vendor_id: vendor.vendor_id
      });
      
      toast.success("Inquiry sent! The vendor will contact you soon.");
      setShowInquiryModal(false);
      setInquiryForm({
        name: user?.name || "",
        email: user?.email || "",
        phone: "",
        event_type: "",
        event_date: "",
        message: ""
      });
    } catch (error) {
      console.error("Error sending inquiry:", error);
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setSendingInquiry(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!vendor) return null;

  const CategoryIcon = categoryIcons[vendor.category] || Sparkles;
  const coverImage = vendor.portfolio_images?.[0] || categoryImages[vendor.category] || "https://images.unsplash.com/photo-1710371281650-6164e4133ae7?w=800&h=600&fit=crop";

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero/Cover */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img 
            src={coverImage}
            alt={vendor.business_name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/50 to-transparent"></div>
        </div>
        
        {/* Back button */}
        <div className="absolute top-24 left-6 z-10">
          <Link to="/vendors">
            <Button 
              variant="outline" 
              size="sm"
              className="bg-black/50 backdrop-blur-sm border-white/10 text-white hover:bg-black/70 rounded-full"
              data-testid="back-to-vendors-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Content */}
      <section className="relative -mt-32 px-6 pb-24">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-charcoal rounded-3xl border border-white/5 overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 md:p-8 border-b border-white/5">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2 bg-gold/10 rounded-full px-3 py-1">
                      <CategoryIcon className="w-4 h-4 text-gold" />
                      <span className="text-sm text-gold capitalize">{vendor.category?.replace("_", " ")}</span>
                    </div>
                    {vendor.is_verified && (
                      <div className="flex items-center gap-1 bg-green-500/10 rounded-full px-3 py-1">
                        <BadgeCheck className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-green-500">Verified</span>
                      </div>
                    )}
                  </div>
                  
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
                    {vendor.business_name}
                  </h1>
                  
                  <div className="flex flex-wrap items-center gap-4 text-white/60">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1 text-gold/60" />
                      {vendor.location}
                    </div>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 mr-1 text-gold fill-gold" />
                      <span className="text-white font-medium">{vendor.rating?.toFixed(1) || "New"}</span>
                      {vendor.review_count > 0 && (
                        <span className="ml-1">({vendor.review_count} reviews)</span>
                      )}
                    </div>
                    <div className="flex items-center text-gold font-bold">
                      <DollarSign className="w-4 h-4" />
                      {vendor.hourly_rate}/hr
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    data-testid="contact-vendor-btn"
                    variant="outline"
                    onClick={handleContact}
                    className="border-white/10 text-white hover:border-gold hover:text-gold rounded-full"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                  <Button
                    data-testid="book-vendor-btn"
                    onClick={handleBookNow}
                    className="bg-gold text-black hover:bg-gold-light rounded-full font-bold gold-glow"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Now
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Tabs */}
            <Tabs defaultValue="about" className="p-6 md:p-8">
              <TabsList className="bg-obsidian border border-white/5 rounded-full p-1 mb-8">
                <TabsTrigger 
                  value="about" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="tab-about"
                >
                  About
                </TabsTrigger>
                <TabsTrigger 
                  value="portfolio" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="tab-portfolio"
                >
                  Portfolio
                </TabsTrigger>
                <TabsTrigger 
                  value="reviews" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="tab-reviews"
                >
                  Reviews
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="about">
                <div className="space-y-8">
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-4">About</h3>
                    <p className="text-white/60 leading-relaxed">{vendor.description}</p>
                  </div>
                  
                  {vendor.services?.length > 0 && (
                    <div>
                      <h3 className="font-heading text-xl font-bold text-white mb-4">Services Offered</h3>
                      <div className="flex flex-wrap gap-2">
                        {vendor.services.map((service, i) => (
                          <span 
                            key={i}
                            className="bg-gold/10 text-gold rounded-full px-4 py-2 text-sm"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <h3 className="font-heading text-xl font-bold text-white mb-4">Pricing</h3>
                    <div className="bg-obsidian rounded-2xl p-6 border border-white/5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white/60 mb-1">Hourly Rate</p>
                          <p className="font-heading text-3xl font-bold text-gold">${vendor.hourly_rate}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white/60 mb-1">Deposit Required</p>
                          <p className="text-white">25% of total</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="portfolio">
                {vendor.portfolio_images?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {vendor.portfolio_images.map((image, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.1 }}
                        className="aspect-square rounded-xl overflow-hidden"
                      >
                        <img 
                          src={image} 
                          alt={`Portfolio ${i + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Camera className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                    <p className="text-white/60">No portfolio images yet</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="reviews">
                {reviews.length > 0 ? (
                  <div className="space-y-4">
                    {/* Rating Summary */}
                    <div className="bg-obsidian rounded-xl p-6 border border-white/5 mb-6">
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="font-heading text-4xl font-bold text-gold">{vendor.rating?.toFixed(1) || "0.0"}</p>
                          <div className="flex gap-1 mt-2 justify-center">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i}
                                className={`w-4 h-4 ${i < Math.round(vendor.rating || 0) ? "text-gold fill-gold" : "text-white/20"}`}
                              />
                            ))}
                          </div>
                          <p className="text-white/40 text-sm mt-1">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="flex-1 border-l border-white/10 pl-6">
                          <p className="text-white/60 text-sm">Based on verified bookings through Rafha Entertainment</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Individual Reviews */}
                    {reviews.map((review, i) => (
                      <motion.div
                        key={review.review_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-obsidian rounded-xl p-6 border border-white/5"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              {[...Array(5)].map((_, j) => (
                                <Star 
                                  key={j}
                                  className={`w-4 h-4 ${j < review.rating ? "text-gold fill-gold" : "text-white/20"}`}
                                />
                              ))}
                            </div>
                            {review.title && (
                              <p className="text-white font-medium mt-2">{review.title}</p>
                            )}
                          </div>
                          <span className="text-white/40 text-sm">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-white/60 mb-3">{review.comment}</p>
                        <div className="flex items-center gap-2 text-white/40 text-sm">
                          <BadgeCheck className="w-4 h-4 text-green-400" />
                          <span>Verified Booking</span>
                          <span className="mx-2">•</span>
                          <span>{review.customer?.name || "Anonymous"}</span>
                        </div>
                        
                        {/* Vendor Response */}
                        {review.vendor_response && (
                          <div className="mt-4 pl-4 border-l-2 border-gold/30 bg-gold/5 rounded-r-lg p-3">
                            <p className="text-gold text-sm font-medium mb-1">Response from {vendor.business_name}</p>
                            <p className="text-white/60 text-sm">{review.vendor_response}</p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Star className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                    <p className="text-white/60 mb-2">No reviews yet</p>
                    <p className="text-white/40 text-sm">Be the first to book and review this vendor!</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </section>
      
      {/* Inquiry Modal */}
      <Dialog open={showInquiryModal} onOpenChange={setShowInquiryModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Contact {vendor?.business_name}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitInquiry} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-white/60 text-sm">Your Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={inquiryForm.name}
                  onChange={handleInquiryChange}
                  required
                  className="bg-white/5 border-white/10 text-white mt-1"
                  data-testid="inquiry-name"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-white/60 text-sm">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={inquiryForm.phone}
                  onChange={handleInquiryChange}
                  required
                  className="bg-white/5 border-white/10 text-white mt-1"
                  data-testid="inquiry-phone"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="text-white/60 text-sm">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={inquiryForm.email}
                onChange={handleInquiryChange}
                required
                className="bg-white/5 border-white/10 text-white mt-1"
                data-testid="inquiry-email"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event_type" className="text-white/60 text-sm">Event Type</Label>
                <Input
                  id="event_type"
                  name="event_type"
                  value={inquiryForm.event_type}
                  onChange={handleInquiryChange}
                  required
                  placeholder="Wedding, Birthday, etc."
                  className="bg-white/5 border-white/10 text-white mt-1"
                  data-testid="inquiry-event-type"
                />
              </div>
              <div>
                <Label htmlFor="event_date" className="text-white/60 text-sm">Event Date</Label>
                <Input
                  id="event_date"
                  name="event_date"
                  type="date"
                  value={inquiryForm.event_date}
                  onChange={handleInquiryChange}
                  required
                  className="bg-white/5 border-white/10 text-white mt-1"
                  data-testid="inquiry-date"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="message" className="text-white/60 text-sm">Message</Label>
              <Textarea
                id="message"
                name="message"
                value={inquiryForm.message}
                onChange={handleInquiryChange}
                required
                rows={4}
                placeholder="Tell us about your event and what you're looking for..."
                className="bg-white/5 border-white/10 text-white mt-1"
                data-testid="inquiry-message"
              />
            </div>
            
            <Button
              type="submit"
              disabled={sendingInquiry}
              className="w-full btn-luxury bg-gold text-black rounded-full py-4 font-bold"
              data-testid="send-inquiry-btn"
            >
              {sendingInquiry ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Inquiry
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default VendorDetail;
